import {
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Divider,
	Grid,
	Icon,
	IconButton,
	Paper,
	Stack,
	Tab,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableFooter,
	TableHead,
	TableRow,
	Tabs,
	Tooltip,
	Typography,
	useTheme,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { ICronJob, IntegratorService } from "../../../shared/services/api/integrator/IntegratorService";
import { AnalysisQueueTab } from "./AnalysisQueueTab";
import { checkByProfileGuard } from "../../../shared/guards/ProfileGuard";
import { PermissionActionsEnum, PermissionRolesEnum } from "../../../shared/enums/profiles";
import { LayoutBaseDePagina } from "../../../shared/layouts";
import { useToast } from "../../../shared/hooks";

// ─── Status helpers ───────────────────────────────────────────────────────────

type CronStatus = "active" | "paused" | "disabled";

const getCronStatus = (cron: ICronJob): CronStatus => {
	if (!cron.enabled) return "disabled";
	if (cron.paused)   return "paused";
	return "active";
};

const statusColor: Record<CronStatus, string> = {
	active:   "#43b897",
	paused:   "#ed6c02",
	disabled: "#e36b5b",
};
const statusBackground: Record<CronStatus, string> = {
	active:   "#e9f8f5",
	paused:   "#fff4e5",
	disabled: "#fcedea",
};
const statusLabel: Record<CronStatus, string> = {
	active:   "Ativo",
	paused:   "Pausado",
	disabled: "Desabilitado",
};

// ─── Action types ─────────────────────────────────────────────────────────────

type ConfirmAction =
	| { type: "run" | "enable" | "disable" | "pause" | "reload" | "delete"; cronId: string; cronName: string }
	| { type: "reloadAll" | "pauseAll" | "stopAll" };

const actionLabels: Record<string, string> = {
	run:       "Executar / Reprocessar",
	enable:    "Habilitar",
	disable:   "Desabilitar",
	pause:     "Pausar",
	reload:    "Recarregar",
	delete:    "Remover",
	reloadAll: "Recarregar todos",
	pauseAll:  "Pausar todos",
	stopAll:   "Parar todos",
};

// ─── Confirm Dialog ───────────────────────────────────────────────────────────

interface ConfirmDialogProps {
	action: ConfirmAction | null;
	loading: boolean;
	onConfirm: () => void;
	onClose: () => void;
}

const ConfirmDialog = ({ action, loading, onConfirm, onClose }: ConfirmDialogProps) => {
	if (!action) return null;
	const isDestructive = action.type === "delete" || action.type === "stopAll" || action.type === "disable";
	const label = actionLabels[action.type];
	const target = "cronId" in action ? `"${action.cronName}"` : "todos os crons";

	return (
		<Dialog open onClose={onClose} maxWidth="xs" fullWidth>
			<DialogTitle sx={{ fontWeight: 400, color: "primary.main" }}>{label}</DialogTitle>
			<Divider />
			<DialogContent>
				<DialogContentText>
					Confirma a ação <strong>{label}</strong> para {target}?
				</DialogContentText>
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
				<Button variant="outlined" onClick={onClose} disabled={loading}>Cancelar</Button>
				<Button
					variant="contained"
					color={isDestructive ? "error" : "primary"}
					onClick={onConfirm}
					disabled={loading}
					startIcon={loading ? <CircularProgress size={14} color="inherit" /> : undefined}
				>
					Confirmar
				</Button>
			</DialogActions>
		</Dialog>
	);
};

// ─── Main ─────────────────────────────────────────────────────────────────────

export const IntegratorQueuePresentation = () => {
	const theme = useTheme();
	const toastHandler = useToast();

	const [tabIndex, setTabIndex] = useState(0);

	const [crons, setCrons]           = useState<ICronJob[]>([]);
	const [isLoading, setIsLoading]   = useState(false);
	const [pendingAction, setPendingAction] = useState<ConfirmAction | null>(null);
	const [actionLoading, setActionLoading] = useState(false);

	const canView    = checkByProfileGuard(PermissionRolesEnum.IntegratorOptions, PermissionActionsEnum.View);
	const canRun     = checkByProfileGuard(PermissionRolesEnum.IntegratorOptions, PermissionActionsEnum.CronRun);
	const canEnable  = checkByProfileGuard(PermissionRolesEnum.IntegratorOptions, PermissionActionsEnum.CronEnable);
	const canDisable = checkByProfileGuard(PermissionRolesEnum.IntegratorOptions, PermissionActionsEnum.CronDisable);
	const canPause   = checkByProfileGuard(PermissionRolesEnum.IntegratorOptions, PermissionActionsEnum.CronPause);
	const canReload  = checkByProfileGuard(PermissionRolesEnum.IntegratorOptions, PermissionActionsEnum.CronReload);
	const canDelete  = checkByProfileGuard(PermissionRolesEnum.IntegratorOptions, PermissionActionsEnum.CronDelete);
	const canGlobal  = checkByProfileGuard(PermissionRolesEnum.IntegratorOptions, PermissionActionsEnum.CronGlobalActions);

	const loadCrons = useCallback(async () => {
		setIsLoading(true);
		const result = await IntegratorService.getAll();
		if (result instanceof Error) {
			toastHandler.present({ type: "error", messageOrContent: result.message });
		} else {
			setCrons(result);
		}
		setIsLoading(false);
	}, []);

	useEffect(() => { if (canView) loadCrons(); }, [canView, loadCrons]);

	const handleConfirm = async () => {
		if (!pendingAction) return;
		setActionLoading(true);

		let result: void | Error | undefined;
		switch (pendingAction.type) {
			case "run":       result = await IntegratorService.run(pendingAction.cronId);    break;
			case "enable":    result = await IntegratorService.enable(pendingAction.cronId);  break;
			case "disable":   result = await IntegratorService.disable(pendingAction.cronId); break;
			case "pause":     result = await IntegratorService.pause(pendingAction.cronId);   break;
			case "reload":    result = await IntegratorService.reload(pendingAction.cronId);  break;
			case "delete":    result = await IntegratorService.remove(pendingAction.cronId);  break;
			case "reloadAll": result = await IntegratorService.reloadAll(); break;
			case "pauseAll":  result = await IntegratorService.pauseAll();  break;
			case "stopAll":   result = await IntegratorService.stopAll();   break;
		}

		if (result instanceof Error) {
			toastHandler.present({ type: "error", messageOrContent: result.message });
		} else {
			toastHandler.present({ type: "success", messageOrContent: `${actionLabels[pendingAction.type]} executado com sucesso.` });
			await loadCrons();
		}

		setPendingAction(null);
		setActionLoading(false);
	};

	const btnSx = {
		borderRadius: 1.5,
		p: 0.5,
		backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : theme.palette.grey[100],
		"&:hover": {
			backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.12)" : theme.palette.grey[200],
		},
	};

	return (
		<LayoutBaseDePagina
			titulo="Fila de Processamento"
			subtitulo="Gerencie os crons e tarefas agendadas do sistema"
		>
			{/* Navegação entre abas */}
			<Box sx={{ display: "flex", alignItems: "center", mx: 1 }}>
				<Tabs
					value={tabIndex}
					onChange={(_, v) => setTabIndex(v)}
					sx={{
						flex: 1,
						borderBottom: 1,
						borderColor: "divider",
						bgcolor: "background.paper",
						borderRadius: 1,
					}}
				>
					<Tab label="Cron Dinâmico" />
					<Tab label="Fila de Análise" />
				</Tabs>
			</Box>

			{/* ── Aba 0 — Cron Dinâmico ─────────────────────────────────────── */}
			{tabIndex === 0 && (
				<>
					{/* Toolbar */}
					<Box
						component={Paper}
						elevation={0}
						sx={{ mx: 1, mt: 2, px: 2, py: 1.5, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}
					>
						<Button
							variant="contained"
							color="primary"
							size="small"
							startIcon={isLoading ? <CircularProgress size={14} color="inherit" /> : <Icon fontSize="small">refresh</Icon>}
							onClick={loadCrons}
							disabled={isLoading}
						>
							Atualizar
						</Button>

						<Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
							{crons.length} {crons.length === 1 ? "registro" : "registros"}
						</Typography>

						{canGlobal && (
							<>
								<Box sx={{ flex: 1 }} />
								<Button variant="outlined" size="small" color="warning"
									startIcon={<Icon fontSize="small">pause_circle</Icon>}
									onClick={() => setPendingAction({ type: "pauseAll" })}
								>
									Pausar todos
								</Button>
								<Button variant="outlined" size="small" color="info"
									startIcon={<Icon fontSize="small">sync_problem</Icon>}
									onClick={() => setPendingAction({ type: "reloadAll" })}
								>
									Recarregar todos
								</Button>
								<Button variant="outlined" size="small" color="error"
									startIcon={<Icon fontSize="small">stop_circle</Icon>}
									onClick={() => setPendingAction({ type: "stopAll" })}
								>
									Parar todos
								</Button>
							</>
						)}
					</Box>

					{/* Tabela Cron */}
					<TableContainer
						component={Paper}
						variant="elevation"
						sx={{
							mt: 1,
							width: "96%",
							px: { xs: 1, sm: 3 },
							py: { xs: 2, sm: 3 },
							ml: 1,
							borderRadius: 2,
							overflow: "auto",
						}}
					>
						<Table
							size="small"
							stickyHeader
							aria-label="fila de processamento"
							sx={{
								"& .MuiTableCell-head": {
									backgroundColor: "grey.100",
									color: "text.secondary",
									borderBottomColor: "#f1f1f1",
								},
								"& .MuiTableCell-root": { borderBottomColor: "#f1f1f1" },
								"& tbody .MuiTableCell-root": { py: 1 },
							}}
						>
							<TableHead>
								<TableRow>
									<TableCell><Typography variant="button" color="text.secondary">Nome</Typography></TableCell>
									<TableCell><Typography variant="button" color="text.secondary">Handler</Typography></TableCell>
									<TableCell><Typography variant="button" color="text.secondary">Schedule</Typography></TableCell>
									<TableCell><Typography variant="button" color="text.secondary">Status</Typography></TableCell>
									<TableCell align="right"><Typography variant="button" color="text.secondary">Ações</Typography></TableCell>
								</TableRow>
							</TableHead>

							<TableBody>
								{isLoading && (
									<TableRow>
										<TableCell colSpan={5} align="center" sx={{ py: 6 }}>
											<Stack alignItems="center" gap={1}>
												<CircularProgress size={32} />
												<Typography variant="body2" color="text.secondary">Carregando crons...</Typography>
											</Stack>
										</TableCell>
									</TableRow>
								)}

								{!isLoading && crons.length === 0 && (
									<TableRow>
										<TableCell colSpan={5} align="center" sx={{ py: 6 }}>
											<Stack alignItems="center" gap={1}>
												<Icon sx={{ fontSize: 40, color: "text.disabled" }}>sync_disabled</Icon>
												<Typography color="text.secondary">Nenhum cron encontrado.</Typography>
											</Stack>
										</TableCell>
									</TableRow>
								)}

								{!isLoading && crons.map((cron) => {
									const status = getCronStatus(cron);
									return (
										<TableRow key={cron.id} hover>
											<TableCell>
												<Typography variant="subtitle2" fontWeight={600}>{cron.name}</Typography>
												<Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>{cron.id}</Typography>
											</TableCell>

											<TableCell>
												<Typography variant="body2" color="text.secondary">{cron.handler}</Typography>
											</TableCell>

											<TableCell>
												<Typography
													variant="body2"
													fontFamily="monospace"
													sx={{
														px: 1, py: 0.25, borderRadius: 1,
														backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "#f5f5f5",
														display: "inline-block", fontSize: "0.75rem",
													}}
												>
													{cron.schedule}
												</Typography>
											</TableCell>

											<TableCell>
												<Chip
													size="small"
													label={statusLabel[status]}
													sx={{
														color: statusColor[status],
														fontWeight: 500,
														background: statusBackground[status],
														border: "0.5px solid #f1f1f1",
														borderRadius: "999px",
														height: 22,
														"& .MuiChip-label": { px: 1.25, fontSize: "0.72rem" },
													}}
												/>
											</TableCell>

											<TableCell align="right">
												<Stack direction="row" justifyContent="flex-end" gap={0.5}>
													{canRun && (
														<Tooltip title="Executar agora">
															<IconButton size="small" sx={{ ...btnSx, color: statusColor.active }}
																onClick={() => setPendingAction({ type: "run", cronId: cron.id, cronName: cron.name })}>
																<Icon fontSize="small">play_arrow</Icon>
															</IconButton>
														</Tooltip>
													)}
													{canEnable && !cron.enabled && (
														<Tooltip title="Habilitar">
															<IconButton size="small" sx={{ ...btnSx, color: statusColor.active }}
																onClick={() => setPendingAction({ type: "enable", cronId: cron.id, cronName: cron.name })}>
																<Icon fontSize="small">power_settings_new</Icon>
															</IconButton>
														</Tooltip>
													)}
													{canDisable && cron.enabled && (
														<Tooltip title="Desabilitar">
															<IconButton size="small" sx={{ ...btnSx, color: statusColor.paused }}
																onClick={() => setPendingAction({ type: "disable", cronId: cron.id, cronName: cron.name })}>
																<Icon fontSize="small">sync_disabled</Icon>
															</IconButton>
														</Tooltip>
													)}
													{canPause && cron.enabled && !cron.paused && (
														<Tooltip title="Pausar">
															<IconButton size="small" sx={{ ...btnSx, color: statusColor.paused }}
																onClick={() => setPendingAction({ type: "pause", cronId: cron.id, cronName: cron.name })}>
																<Icon fontSize="small">pause_circle</Icon>
															</IconButton>
														</Tooltip>
													)}
													{canReload && (
														<Tooltip title="Recarregar">
															<IconButton size="small" sx={{ ...btnSx, color: "#1F618D" }}
																onClick={() => setPendingAction({ type: "reload", cronId: cron.id, cronName: cron.name })}>
																<Icon fontSize="small">refresh</Icon>
															</IconButton>
														</Tooltip>
													)}
													{canDelete && (
														<Tooltip title="Remover">
															<IconButton size="small" sx={{ ...btnSx, color: statusColor.disabled }}
																onClick={() => setPendingAction({ type: "delete", cronId: cron.id, cronName: cron.name })}>
																<Icon fontSize="small">delete_outline</Icon>
															</IconButton>
														</Tooltip>
													)}
												</Stack>
											</TableCell>
										</TableRow>
									);
								})}
							</TableBody>

							{!isLoading && crons.length > 0 && (
								<TableFooter>
									<TableRow>
										<TableCell colSpan={5}>
											<Stack direction="row" gap={2} flexWrap="wrap" py={0.5}>
												{(["active", "paused", "disabled"] as CronStatus[]).map((s) => {
													const count = crons.filter((c) => getCronStatus(c) === s).length;
													return (
														<Stack key={s} direction="row" alignItems="center" gap={0.5}>
															<Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: statusColor[s] }} />
															<Typography variant="caption" color="text.secondary">
																{statusLabel[s]}: <strong>{count}</strong>
															</Typography>
														</Stack>
													);
												})}
											</Stack>
										</TableCell>
									</TableRow>
								</TableFooter>
							)}
						</Table>
					</TableContainer>

					<ConfirmDialog
						action={pendingAction}
						loading={actionLoading}
						onClose={() => !actionLoading && setPendingAction(null)}
						onConfirm={handleConfirm}
					/>
				</>
			)}

			{/* ── Aba 1 — Fila de Análise ───────────────────────────────────── */}
			{tabIndex === 1 && <AnalysisQueueTab />}
		</LayoutBaseDePagina>
	);
};
